import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../hooks/useAuth';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// GQL
import { client } from '../../../../gql/client';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { GET_GENERIC_ORDERS } from '../../../../gql/queries/genericOrders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';

// Redux
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';

export const LoaderWorkOrdersGeneralView: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
  const cursorGenericOrders = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(
    store.getState()
  );
  const dispatch = store.dispatch;

  const roleActive = userData?.user?.user?.rolActive;

  // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
  let effectiveConcessionId = cursorGenericOrders.concessionId;
  if (!effectiveConcessionId) {
    try {
      const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
      if (concessions?.length === 1) {
        effectiveConcessionId = concessions[0].id;
        dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, { ...cursorGenericOrders, concessionId: effectiveConcessionId }));
      }
    } catch { /* continue without concessionId */ }
  }

  if (userData?.user) {
    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido. HIT path no podía cerrarlo y la tabla quedaba
    // bloqueada permanentemente.
    dispatch(loadingData(tableIds.WORKORDER_GENERAL_VIEW, false));

    console.log(`%c[LOADER] Redux cursor=${cursorGenericOrders.cursor ? cursorGenericOrders.cursor.slice(0,8)+'…' : "''"} take=${cursorGenericOrders.take} concession=${effectiveConcessionId?.slice(0,8) ?? 'none'}`, 'color: #cc00cc; font-weight: bold');
    const readGenericOrders = client.readQuery({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: cursorGenericOrders.take,
          cursor: cursorGenericOrders.cursor,
          concessionId: effectiveConcessionId,
          teamIds: cursorGenericOrders.teamIds ?? [],
          capitolId: cursorGenericOrders.capitolId,
          subCapitolId: cursorGenericOrders.subCapitolId,
          sortField: cursorGenericOrders.sortField,
          orderBy: cursorGenericOrders.orderBy ?? 'desc',
          toDate: cursorGenericOrders.toDate,
          fromDate: cursorGenericOrders.fromDate,
          indexType: cursorGenericOrders.indexType,
          indexExpedition: cursorGenericOrders.indexExpedition,
          classification: cursorGenericOrders.classification,
          predefinedOrderId: cursorGenericOrders.predefinedOrderId,
          states: cursorGenericOrders.states,
          userIds: cursorGenericOrders.userIds,
          pkInitKm: cursorGenericOrders.pkInitKm,
          pkInitMeter: cursorGenericOrders.pkInitMeter,
          pkEndKm: cursorGenericOrders.pkEndKm,
          pkEndMeter: cursorGenericOrders.pkEndMeter,
          description: cursorGenericOrders.description,
          track: cursorGenericOrders.track,
          onlyFlagged: cursorGenericOrders.onlyFlagged,
        },
      },
    });

    const pageInfo = readGenericOrders?.genericOrders?.pageInfo;
    const totalCount = readGenericOrders?.genericOrders?.totalCount;
    const isCacheEmpty = JSON.stringify(readGenericOrders) === '{}';
    const itemCount = readGenericOrders?.genericOrders?.genericOrders?.length ?? 0;
    console.log(`%c[LOADER] readQuery → ${!readGenericOrders ? 'NULL' : isCacheEmpty ? 'EMPTY {}' : `HIT ${itemCount} items`} | totalCount=${totalCount} pageInfo=${JSON.stringify(pageInfo)}`, 'color: #cc00cc; font-weight: bold');

    const genericOrders =
      readGenericOrders &&
      !isCacheEmpty &&
      (await selectAllOrders(readGenericOrders?.genericOrders?.genericOrders));

    const [
      {
        data: { capitols },
      },
      {
        data: { concessions },
      },
      {
        data: { subCapitols },
      },
      {
        data: { classifications },
      },
      {
        data: { teams },
      },
      {
        data: { predefinedOrders },
      },
      {
        data: { users },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_CAPITOLS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: GET_CONCESSIONS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_CLASSIFICATIONS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_TEAMS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const filtersDataOptions = {
      capitols,
      concessions,
      subCapitols,
      classifications,
      teams,
      predefinedOrders,
      users,
    };

    const combinedData = {
      genericOrders,
      filtersDataOptions,
      totalCount,
      pageInfo,
      roleActive,
    };

    if (!readGenericOrders || isCacheEmpty) {
      console.log(`%c[LOADER] CACHE MISS → NETWORK FETCH cursor=${cursorGenericOrders.cursor ? cursorGenericOrders.cursor.slice(0,8)+'…' : "''"} take=${cursorGenericOrders.take}`, 'color: #cc00cc; font-weight: bold');
      dispatch(loadingData(tableIds.WORKORDER_GENERAL_VIEW, true));
      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(cursorGenericOrders.take, takeBase);
        data = await client.query({
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              concessionId: effectiveConcessionId,
              teamIds: cursorGenericOrders.teamIds ?? [],
              capitolId: cursorGenericOrders.capitolId,
              subCapitolId: cursorGenericOrders.subCapitolId,
              sortField: cursorGenericOrders.sortField,
              orderBy: cursorGenericOrders.orderBy ?? 'desc',
              toDate: cursorGenericOrders.toDate,
              fromDate: cursorGenericOrders.fromDate,
              indexType: cursorGenericOrders.indexType,
              indexExpedition: cursorGenericOrders.indexExpedition,
              classification: cursorGenericOrders.classification,
              predefinedOrderId: cursorGenericOrders.predefinedOrderId,
              states: cursorGenericOrders.states,
              userIds: cursorGenericOrders.userIds,
              pkInitKm: cursorGenericOrders.pkInitKm,
              pkInitMeter: cursorGenericOrders.pkInitMeter,
              pkEndKm: cursorGenericOrders.pkEndKm,
              pkEndMeter: cursorGenericOrders.pkEndMeter,
              description: cursorGenericOrders.description,
              track: cursorGenericOrders.track,
              onlyFlagged: cursorGenericOrders.onlyFlagged,
            },
          },
          fetchPolicy: 'cache-first',
        });

        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          concessionId: effectiveConcessionId,
          teamIds: cursorGenericOrders.teamIds ?? [],
          capitolId: cursorGenericOrders.capitolId,
          subCapitolId: cursorGenericOrders.subCapitolId,
          sortField: cursorGenericOrders.sortField,
          orderBy: cursorGenericOrders.orderBy ?? 'desc',
          toDate: cursorGenericOrders.toDate,
          fromDate: cursorGenericOrders.fromDate,
          indexType: cursorGenericOrders.indexType,
          indexExpedition: cursorGenericOrders.indexExpedition,
          classification: cursorGenericOrders.classification,
          predefinedOrderId: cursorGenericOrders.predefinedOrderId,
          states: cursorGenericOrders.states,
          userIds: cursorGenericOrders.userIds,
          pkInitKm: cursorGenericOrders.pkInitKm,
          pkInitMeter: cursorGenericOrders.pkInitMeter,
          pkEndKm: cursorGenericOrders.pkEndKm,
          pkEndMeter: cursorGenericOrders.pkEndMeter,
          description: cursorGenericOrders.description,
          track: cursorGenericOrders.track,
          onlyFlagged: cursorGenericOrders.onlyFlagged,
        };

        dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, setCursorData));
      } else {
        data = await client.query({
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: cursorGenericOrders.take,
              cursor: cursorGenericOrders.cursor,
              concessionId: effectiveConcessionId,
              teamIds: cursorGenericOrders.teamIds ?? [],
              capitolId: cursorGenericOrders.capitolId,
              subCapitolId: cursorGenericOrders.subCapitolId,
              sortField: cursorGenericOrders.sortField,
              orderBy: cursorGenericOrders.orderBy ?? 'desc',
              toDate: cursorGenericOrders.toDate,
              fromDate: cursorGenericOrders.fromDate,
              indexType: cursorGenericOrders.indexType,
              indexExpedition: cursorGenericOrders.indexExpedition,
              classification: cursorGenericOrders.classification,
              predefinedOrderId: cursorGenericOrders.predefinedOrderId,
              states: cursorGenericOrders.states,
              userIds: cursorGenericOrders.userIds,
              pkInitKm: cursorGenericOrders.pkInitKm,
              pkInitMeter: cursorGenericOrders.pkInitMeter,
              pkEndKm: cursorGenericOrders.pkEndKm,
              pkEndMeter: cursorGenericOrders.pkEndMeter,
              description: cursorGenericOrders.description,
              track: cursorGenericOrders.track,
              onlyFlagged: cursorGenericOrders.onlyFlagged,
            },
          },
          fetchPolicy: 'cache-first',
        });
      }

      const loading = data.loading;
      const pageInfo = data.data.genericOrders.pageInfo;
      const totalCount = data.data.genericOrders.totalCount;

      const genericOrders = await selectAllOrders(
        data.data.genericOrders.genericOrders
      );

      const [
        {
          data: { capitols },
        },
        {
          data: { concessions },
        },
        {
          data: { subCapitols },
        },
        {
          data: { classifications },
        },
        {
          data: { teams },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { users },
        },
      ] = await Promise.all([
        client.query({
          query: FETCH_CAPITOLS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: GET_CONCESSIONS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_TEAMS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const filtersDataOptions = {
        capitols,
        concessions,
        subCapitols,
        classifications,
        teams,
        predefinedOrders,
        users,
      };

      const combinedData = {
        genericOrders,
        filtersDataOptions,
        totalCount,
        pageInfo,
        loading,
        roleActive,
      };

      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(loadingData(tableIds.WORKORDER_GENERAL_VIEW, false));
      }
    }

    return combinedData;
  }
  return {};
  } catch (err) {
    console.log('Loader offline fallback:', err);
    // FIX (loader-loading-flag-leak): defensa para errores antes del MISS branch.
    store.dispatch(loadingData(tableIds.WORKORDER_GENERAL_VIEW, false));
    return { genericOrders: [], filtersDataOptions: { capitols: [], concessions: [], subCapitols: [], classifications: [], teams: [], predefinedOrders: [], users: [] }, totalCount: 0, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
  }
};

  return { data: load() };
};
